import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { FC, Fragment, useMemo } from 'react';
import { CustomTypography } from 'shared/elements/CustomTypography';
import { CheckBox } from '@room-match/shared-ui-components';
import { IPetFormProps, PetCheckBoxProps } from 'shared/interfaces/IPets';
import { generateMonthRange } from 'shared/utils/generateMonthRange';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ClearIcon from '@material-ui/icons/Clear';
import { useStyles } from 'pages/MyInfo/MyInfo.styles';
interface PetFormComponent {
  petForms: IPetFormProps[];
  onSave?: VoidFunction;
  disableSaveButton?: boolean;
  checkBoxOptions: PetCheckBoxProps;
  imageLength: number;
}

interface IAutocompleteFormProps {
  form: IPetFormProps;
  checkBoxOptions: PetCheckBoxProps;
}

const VARIABLE_NAMES = {
  animal: 'Animal',
  age: 'Age',
};

const month_ranges = generateMonthRange();

const AutocompleteForm: FC<IAutocompleteFormProps> = ({ form, checkBoxOptions }) => {
  const classes = useStyles();
  const isAgeForm = form?.name === VARIABLE_NAMES.age;
  const inputProps =
    form.name === VARIABLE_NAMES.age
      ? {
          readOnly: form?.readOnly,
          min: 0,
          max: 99.9,
        }
      : undefined;
  const InputProps =
    form?.othersMode && form.name !== VARIABLE_NAMES.age
      ? {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton style={{ fontSize: '1.25rem', padding: 1.2 }} onClick={() => form?.handleOnChange('')}>
                <ClearIcon fontSize="inherit" />
              </IconButton>
            </InputAdornment>
          ),
        }
      : undefined;

  // If I didnt wrap in useMemo the checkbox is not re-rendering
  const isShowMonths = useMemo(() => checkBoxOptions?.ageInMonths, [checkBoxOptions?.ageInMonths]);

  return (
    <Fragment>
      <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <CustomTypography fontSize={14}>{form.name}</CustomTypography>
        {isAgeForm ? (
          <Box style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckBox
              checked={form?.disable ? false : isShowMonths}
              style={{ padding: 0 }}
              onChange={checkBoxOptions?.handleShowMonths}
              disabled={form.disable}
            />
            <CustomTypography fontSize={10}>In Months</CustomTypography>
          </Box>
        ) : null}
      </Box>

      {form?.name !== VARIABLE_NAMES.age && !form?.othersMode ? (
        <Box className={classes.autoCompleteWrapper}>
          <Autocomplete
            value={form.value}
            open={form.othersMode !== undefined && Boolean(form?.othersMode) ? false : undefined}
            noOptionsText={'No Options'}
            popupIcon={
              form?.isLoading ? <CircularProgress size={12} style={{ marginTop: 5 }} /> : <ArrowDropDownIcon />
            }
            options={form?.list}
            getOptionLabel={(option) => option}
            getOptionSelected={(option, value) => option === value}
            onInputChange={(event, newInputValue) => {
              if (form?.name === VARIABLE_NAMES.age) {
                form?.handleInputChange?.(newInputValue);
              }
            }}
            onChange={(event, newValue) => form.handleOnChange(newValue || '')}
            disabled={form?.disable}
            freeSolo={form?.othersMode}
            renderInput={(params) => (
              <TextField
                placeholder={form?.placeholder || 'Select'}
                {...params}
                variant="outlined"
                inputProps={{
                  ...params.inputProps,
                  readOnly: form?.readOnly,
                }}
              />
            )}
          />
        </Box>
      ) : form?.othersMode || form.name === VARIABLE_NAMES.age ? (
        <Fragment>
          {checkBoxOptions?.ageInMonths && isAgeForm ? (
            // This Autocomplete is for Checkbox options, i intentionally seperate it from above, its easier to maintain and track
            <Box className={classes.autoCompleteWrapper}>
              <Autocomplete
                options={month_ranges}
                value={form?.value}
                getOptionLabel={(option) => option}
                fullWidth
                inputValue={form?.value}
                onInputChange={(event, newInputValue) => {
                  if (form?.handleInputChange) form?.handleInputChange(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    placeholder={'Select'}
                    {...params}
                    type="number"
                    variant="outlined"
                    className="age-field-pet"
                    onKeyDown={(e) => {
                      if (form.name !== VARIABLE_NAMES.age) return;

                      if (['e', 'E', '-', ',', '.'].includes(e.key) || (Number(form?.value) === 0 && e.key === '0')) {
                        e.preventDefault();
                      }
                    }}
                    inputProps={{
                      ...params.inputProps,
                    }}
                  />
                )}
                disabled={form?.disable}
                onChange={(event, newValue) => checkBoxOptions?.handleMonthsChange(newValue)}
              />
            </Box>
          ) : (
            <TextField
              placeholder={form?.placeholder || 'Select'}
              fullWidth
              variant="outlined"
              type={form?.name === VARIABLE_NAMES.age ? 'number' : 'text'}
              value={form?.value}
              autoFocus
              onChange={(event) => {
                if (form?.handleInputChange) form?.handleInputChange(event?.target?.value);
              }}
              className="age-field-pet"
              onKeyDown={(e) => {
                if (form.name !== VARIABLE_NAMES.age) return;
                if (Number(form?.value) > 25) return;
                if (['e', 'E', '-', ','].includes(e.key)) {
                  e.preventDefault();
                }
                if (form?.value === `${0}` && e.key === '0') {
                  e.preventDefault();
                }
              }}
              InputProps={InputProps}
              inputProps={inputProps}
              disabled={form?.disable}
            />
          )}
        </Fragment>
      ) : null}
    </Fragment>
  );
};

const PetForm: FC<PetFormComponent> = ({ petForms, onSave, disableSaveButton, checkBoxOptions, imageLength }) => {
  return (
    <Grid container>
      {petForms.map((form) => {
        const isAnimalForm = form?.name === VARIABLE_NAMES.animal && checkBoxOptions?.showCheckBox;
        return (
          <Fragment key={form?.name}>
            <Grid item xs={12}>
              <AutocompleteForm form={form} checkBoxOptions={checkBoxOptions} />
            </Grid>
            {isAnimalForm && (
              <Grid item xs={12}>
                <CustomTypography fontSize={14}>{'Pet Health and Behavior'}</CustomTypography>
                <FormGroup row>
                  {checkBoxOptions?.list?.map((checkbox: string) => (
                    <FormControlLabel
                      key={checkbox}
                      control={
                        <CheckBox
                          onChange={() => checkBoxOptions.handleChange(checkbox)}
                          checked={checkBoxOptions?.isChecked(checkbox)}
                          name={checkbox}
                        />
                      }
                      label={<CustomTypography fontSize={11.5}>{checkbox}</CustomTypography>}
                      style={{ fontSize: 12 }}
                    />
                  ))}
                </FormGroup>
              </Grid>
            )}
          </Fragment>
        );
      })}
      <Grid item xs={12} style={{ marginTop: 5 }}>
        <Tooltip title={`Maximum of 5 image uploads only`} disableHoverListener={imageLength <= 5}>
          <div>
            <Button
              onClick={onSave}
              disabled={disableSaveButton}
              variant="contained"
              color="primary"
              fullWidth
              size="medium"
              style={{ height: '50px', fontSize: 16 }}
            >
              Save
            </Button>
          </div>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

export default PetForm;
